import { ContactFilterApproximation } from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { InfoIcon } from '../../../../web/components/svgs/icons/InfoIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../web/components/Tooltip';
import * as Colors from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	loading?: boolean;
	approximation?: ContactFilterApproximation | null | undefined;
	followUpCount?: number;
	fallbackCount?: number;
	styles?: StyleDeclarationValue[];
	hasUnsubscribeTagNot?: boolean;
	hasEmailBouncedTagNot?: boolean;
}

export const RecipientsCount: React.FC<IProps> = observer(props => {
	const {
		className,
		styles,
		loading,
		followUpCount,
		fallbackCount: manualCount,
		approximation,
		hasUnsubscribeTagNot,
		hasEmailBouncedTagNot,
	} = props;
	// hasEmail can exceed total when filters exclude every match (e.g. policy filters) — clamp so an
	// empty result reads as "0 recipients" instead of "Approx. 1 out of 0 have emails".
	const approxTotal = approximation?.total || 0;
	const approxHasEmail = Math.min(approximation?.hasEmail || 0, approxTotal);
	const hasRecipientsWithoutEmails = approximation ? approxHasEmail !== approxTotal : false;
	const flatCount =
		(approximation ? approxTotal : !Number.isNaN(followUpCount) ? followUpCount : manualCount) ?? (manualCount || 0);

	const recipients = !hasRecipientsWithoutEmails
		? `${flatCount}` + ` recipient${flatCount > 1 || flatCount === 0 ? 's' : ''}`
		: `Approx. ${approxHasEmail} out of ${approxTotal} have emails`;

	let info;
	if (hasUnsubscribeTagNot && hasEmailBouncedTagNot) {
		info = 'Excludes contacts who unsubscribed or whose email bounced.';
	} else if (hasEmailBouncedTagNot) {
		info = 'Excludes contacts whose email bounced.';
	} else if (hasUnsubscribeTagNot) {
		info = 'Excludes contacts who unsubscribed.';
	}

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} recipients-count ${className || ''}`}>
			{loading ? (
				<LoadingSpinner type='tiny' />
			) : (
				<h5 className={css(styleSheet.recipientCountHeader)}>
					<span className={css(styleSheet.recipientCountLabel)}>Sending to: </span>
					<span className={css(styleSheet.recipientCountResult, baseStyleSheet.fontBold)}>{recipients}</span>
					{info ? (
						<Tooltip>
							<TooltipTrigger>
								<InfoIcon fillColor={Colors.brandSecondary} className={css(styleSheet.infoIcon)} />
							</TooltipTrigger>
							<TooltipContent>{info}</TooltipContent>
						</Tooltip>
					) : null}
				</h5>
			)}
		</div>
	);
});
